import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { getMonumentQuestions } from '../utils/monumentQuestions';
import { playCorrectSound, playWrongSound } from '../utils/soundEffects';
import { X, ChevronRight, Landmark, Lightbulb } from 'lucide-react';
import confetti from 'canvas-confetti';

const OPTION_COLORS = [
  'bg-stone-600 border-stone-800 hover:bg-stone-500',
  'bg-amber-600 border-amber-800 hover:bg-amber-500',
  'bg-teal-600 border-teal-800 hover:bg-teal-500',
  'bg-rose-600 border-rose-800 hover:bg-rose-500',
];

export default function MonumentPlayPage() {
  const navigate = useNavigate();
  const [questions, setQuestions] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [score, setScore] = useState(0);
  const [selectedOption, setSelectedOption] = useState(null);
  const [showAnswer, setShowAnswer] = useState(false);
  const scoreRef = useRef(0);

  useEffect(() => { scoreRef.current = score; }, [score]);

  useEffect(() => {
    setQuestions(getMonumentQuestions(15));
  }, []); // mount-only: generate once

  const currentQ = questions[currentIndex];

  const handleOptionClick = (idx) => {
    if (showAnswer) return;
    setSelectedOption(idx);
    setShowAnswer(true);

    if (idx === currentQ.correctIndex) {
      setScore((s) => s + 1);
      playCorrectSound();
      confetti({ particleCount: 50, spread: 60, origin: { y: 0.7 }, colors: ['#a8a29e', '#d97706', '#0d9488', '#e11d48'] });
    } else {
      playWrongSound();
    }
  };

  const handleNext = () => {
    if (currentIndex + 1 >= questions.length) {
      navigate(`/monument/results?score=${scoreRef.current}&total=${questions.length}`);
      return;
    }
    setCurrentIndex((i) => i + 1);
    setSelectedOption(null);
    setShowAnswer(false);
  };

  if (!currentQ) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-stone-50 to-white">
        <p className="font-heading text-2xl text-gray-400">Loading...</p>
      </div>
    );
  }

  const progress = ((currentIndex + 1) / questions.length) * 100;

  return (
    <div className="min-h-screen bg-gradient-to-b from-stone-50 to-amber-50 flex flex-col">
      <div className="bg-white border-b border-gray-100 px-4 py-3">
        <div className="max-w-2xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Landmark className="w-5 h-5 text-stone-600" />
            <span data-testid="monument-score" className="font-heading text-2xl font-black text-gray-900">
              {score}/{questions.length}
            </span>
          </div>
          <span data-testid="monument-counter" className="font-body text-sm font-bold text-gray-500 bg-gray-100 px-3 py-1 rounded-full">
            {currentIndex + 1} of {questions.length}
          </span>
          <button data-testid="monument-quit" onClick={() => navigate('/monument')} className="p-2 rounded-full hover:bg-gray-100 transition-colors">
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>
      </div>

      <div className="h-2 bg-gray-200">
        <div
          data-testid="monument-progress"
          className="h-full bg-gradient-to-r from-stone-500 to-amber-600 rounded-r-full transition-all duration-500 ease-out"
          style={{ width: `${progress}%` }}
        />
      </div>

      <div className="flex-1 flex flex-col max-w-2xl mx-auto w-full px-4 py-5">
        <p className="text-center font-heading text-xl sm:text-2xl font-black text-gray-900 mb-3">
          Which monument is this?
        </p>

        <div className="rounded-3xl overflow-hidden shadow-lg border-4 border-white mb-3 bg-stone-200">
          <img
            data-testid="monument-image"
            key={currentQ.id}
            src={currentQ.image}
            alt="Guess this monument"
            className="w-full h-56 sm:h-72 object-cover"
          />
        </div>

        <div className="flex items-center justify-center gap-2 mb-4">
          <Lightbulb className="w-5 h-5 text-amber-500" />
          <p data-testid="monument-hint" className="font-heading text-base sm:text-lg font-black text-stone-700">
            {currentQ.hint}
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
          {currentQ.options.map((opt, i) => {
            let extraClass = '';
            if (showAnswer) {
              if (i === currentQ.correctIndex) {
                extraClass = 'ring-4 ring-green-400 scale-[1.02]';
              } else if (i === selectedOption) {
                extraClass = 'opacity-60 ring-4 ring-red-400';
              } else {
                extraClass = 'opacity-40';
              }
            }

            return (
              <button
                key={`${currentQ.id}-opt-${opt}`}
                data-testid={`monument-option-${i}`}
                onClick={() => handleOptionClick(i)}
                disabled={showAnswer}
                className={`${OPTION_COLORS[i]} text-white font-bold text-sm sm:text-base py-4 px-4 rounded-2xl border-b-[5px] ${!showAnswer ? 'active:border-b-0 active:translate-y-[5px]' : ''} transition-all shadow-md text-center ${extraClass}`}
              >
                {opt}
              </button>
            );
          })}
        </div>

        {showAnswer && (
          <div className="text-center animate-pop-in pb-6">
            {selectedOption === currentQ.correctIndex ? (
              <p data-testid="monument-feedback-correct" className="font-heading text-xl font-black text-green-600 mb-1">
                Correct!
              </p>
            ) : (
              <p data-testid="monument-feedback-wrong" className="font-heading text-base font-bold text-red-500 mb-1">
                It&apos;s <span className="text-green-700">{currentQ.answer}</span>!
              </p>
            )}
            <p className="font-body text-xs text-gray-500 mb-3">{currentQ.place}</p>
            <button
              data-testid="monument-next"
              onClick={handleNext}
              className="bg-gradient-to-r from-stone-600 to-stone-800 text-white font-black text-base py-3 px-8 rounded-full border-b-[4px] border-stone-950 active:border-b-0 active:translate-y-[4px] transition-all hover:from-stone-500 hover:to-stone-700 inline-flex items-center gap-2"
            >
              {currentIndex + 1 >= questions.length ? 'See Results' : 'Next Monument'}
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
